"use strict";

import { ObjectCreator } from "../objectCreator.js";
import { Application } from "../../main/application.js";
import { Screen } from "../../systems/core/screen.js";
import { TransformHandler } from "../transformHandler.js";

export class Container extends PIXI.Container {
    constructor(config) {
        super();
        this.__objectType = "GameObject";

        this.name = config.name;

        // Create Parenting
        ObjectCreator.createParenting(this, config);

        // Create Anchoring
        ObjectCreator.createAnchoring(this, false, false, config);

        // Set Scale
        ObjectCreator.setScale(this, config);

        // Set Position
        ObjectCreator.setPosition(this, false, config);
        this._refreshPosition();

        // Set Rotation Angle
        ObjectCreator.setRotationAngle(this, config);
        // Set Visibility
        ObjectCreator.setVisibility(this, config);
        // Set Renderability
        ObjectCreator.setRenderability(this, config);

        ObjectCreator.setResolutionListeners(this, true, config);
        ObjectCreator.setOrientationListeners(this, true, config);

        ObjectCreator.setDisposeCallbacks(this);

        if (config.worldPosition !== undefined) {
            this.setWorldPosition(config.worldPosition);
        }

        this._externalBounds = config.externalBounds;

        config.onInitialize?.bind(this)(config);
    }

    dispose() {
        this._onDispose.dispatch();

        while (this.children.length > 0) {
            this.removeChild(this.children[0]).dispose();
        }

        this.renderable = false;
        this.parentContainer.removeChild(this);
    }

    // SetParent
    setParent(newParent) {
        this.parentContainer = newParent;
        this.parentContainer.addChild(this);
        this.onResolutionChanged();
    }

    // Get - Set X/Y
    getX() {
        return this.x;
    }
    getY() {
        return this.y;
    }

    setX(x) {
        this.anchoring.margin.x = x;
        this._refreshPosition();
    }
    setY(y) {
        this.anchoring.margin.y = y;
        this._refreshPosition();
    }

    // Get - Set Position
    getPosition() {
        return { x: this.x, y: this.y };
    }
    setPosition(x,y) {
        this.anchoring.margin.x = x.x ?? x;
        this.anchoring.margin.y = x.y ?? y;
        this._refreshPosition();
    }

    getRealWorldPosition() {
        this.parentContainer.updateTransform();
        return this.parentContainer.toGlobal(this.getPosition());
    }
    getWorldPosition() {
        let position = this.getRealWorldPosition();
        position.x -= Application.main.worldCenterX;
        position.y -= Application.main.worldCenterY;
        return position;
    }
    setRealWorldPosition(x,y) {
        this._tPosition.x = x.x ?? x;
        this._tPosition.y = x.y ?? y;
        this._tPosition = this.parentContainer.toLocal(this._tPosition);
        
        this.anchoring.margin.x = this._tPosition.x;
        this.anchoring.margin.y = this._tPosition.y;

        this._refreshPosition();
    }
    setWorldPosition(x,y) {
        this._tPosition.x = (x.x ?? x) + Application.main.worldCenterX;
        this._tPosition.y = (x.y ?? y) + Application.main.worldCenterY;
        this._tPosition = this.parentContainer.toLocal(this._tPosition);
        
        this.anchoring.margin.x = this._tPosition.x;
        this.anchoring.margin.y = this._tPosition.y;

        this._refreshPosition();
    }

    getWorldPositionScale() {
        return this.lossyScale;
    }
    transformLocalToWorld(value) {
        return value / this.getWorldPositionScale();
    }
    transformLocalToWorldPosition(position) {
        position.x = this.transformLocalToWorld(position.x);
        position.y = this.transformLocalToWorld(position.y);
    }

    _refreshPosition() {
        if (this.anchoring.anchorBounds !== undefined) {
            TransformHandler.updateAnchor(this);
        } else {
            TransformHandler.updateMargin(this);
        }
    }

    setAnchorX(x) {
        this.anchoring.anchor.x = x;
        this._refreshPosition();
    }
    setAnchorY(y) {
        this.anchoring.anchor.y = y;
        this._refreshPosition();
    }
    setAnchor(x,y) {
        this.anchoring.anchor.x = x.x ?? x;
        this.anchoring.anchor.y = x.y ?? y ?? x;
        this._refreshPosition();
    }

    setAnchorBounds(bounds) {
        this.anchoring.anchorBounds = bounds;
        this._refreshPosition();
    }

    // Get - Set Scale
    getScale() {
        return this.scale;
    }
    setScale(x,y) {
        this.scale.set(x, y ?? x);
    }
    setScaleX(x) {
        this.scale.set(x, this.scale.y);
    }
    setScaleY(y) {
        this.scale.set(this.scale.x, y);
    }

    get lossyScaleX() {
        return this.scale.x * (this.parentContainer.lossyScaleX ?? 1);
    }
    get lossyScaleY() {
        return this.scale.y * (this.parentContainer.lossyScaleY ?? 1);
    }
    get lossyScale() {
        return this.lossyScaleY;
    }

    get boundX() {
        return this.getLocalBounds().width * this.scale.x;
    }
    get boundY() {
        return this.getLocalBounds().height * this.scale.y;
    }
    get bounds() {
        return { width: this.boundX, height: this.boundY };
    }
    get boundWithOffsetX() {
        return this.boundX + this.getLocalBounds().x * this.scale.x;
    }
    get boundWithOffsetY() {
        return this.boundY + this.getLocalBounds().y * this.scale.y;
    }
    get boundsWithOffset() {
        return { width: this.boundWithOffsetX, height: this.boundWithOffsetY };
    }

    get externalBounds() {
        return this._externalBounds ?? (this.parentContainer !== undefined ?
            this.parentContainer.externalBounds : { width: 1, height: 1 });
    }
    set externalBounds(value) {
        this._externalBounds = value;
    }
    get unscaledExternalBounds() {
        return Vector2.multiplySize(this.externalBounds, this.scale.y / this.lossyScale);
    }

    scaleValue(value) {
        return value * this.lossyScale * Screen.main.stageScale;
    }

    onResolutionChanged() {
        if (this.parentContainer === undefined) return;
        
        this._onResolutionWillChangeCallback?.();

        this._refreshPosition();

        this._onResolutionChangedCallback?.();

        for (let i = 0; i < this.children.length; i++) {
            this.children[i].onResolutionChanged();
        }

        this._onResolutionChangedAfterCallback?.();
    }

    onOrientationChanged() {
        this._onOrientationWillChangeCallback?.();

        for (let i = 0; i < this.children.length; i++) {
            this.children[i].onOrientationChanged();
        }

        this._onOrientationChangedCallback?.();
    }
}